import type { ApiErrorCode, ApiFieldError } from '@room-mates/shared';

/**
 * A failure the API described on purpose.
 *
 * Carrying the code and the per-field details through to the UI is what lets a
 * form put "Name is required" next to the name box instead of dropping a
 * generic banner at the top of the page.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly details: readonly ApiFieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Messages for one form field, keyed by the path the API reported. */
  messagesFor(path: string): string[] {
    return this.details.filter((detail) => detail.path === path).map((detail) => detail.message);
  }

  get isValidation(): boolean {
    return this.code === 'validation_error' || this.code === 'unprocessable';
  }

  get isUnauthorized(): boolean {
    return this.code === 'unauthorized';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** A message safe to show a user, whatever went wrong. */
export function describeError(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong';
}
